"use client";

import Link from "next/link";
import { ArrowRight } from "@/components/icons";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import Container from "@/components/ui/container";
import { Breadcrumbs, type BreadcrumbItemType } from "@/components/ui/breadcrumbs";
import { AccessoryRecordTable } from "@/components/accessories/accessory-record-table";
import { Skeleton } from "@/components/ui/skeleton";
import {
 ACCESSORY_DESCRIPTIONS,
 ACCESSORY_KEY_SPECS,
} from "@/lib/constants/accessory-descriptions";
import {
 getAccessoryColumnDefinitions,
} from "@/lib/constants/accessory-columns";
import { AccessoryEmptyCTA } from "../_components/accessory-empty-cta";

const PARENT_TYPE_NAMES: Record<string, string> = {
 "ibc-totes": "IBC Tote",
 "metal-drums": "Metal Drum",
 "plastic-drums": "Plastic Drum",
 drums: "Drum",
 "gaylord-boxes": "Gaylord Box",
 "bulk-bags": "Bulk Bag",
 pallets: "Pallet",
};

function LoadingSkeleton() {
 return (
  <div className="space-y-8">
   {Array.from({ length: 3 }).map((_, outerIndex) => (
    <div key={outerIndex} className="space-y-3">
     <Skeleton className="h-6 w-48" />
     <div className="rounded-lg border">
      <div className="space-y-3 p-4">
       {Array.from({ length: 4 }).map((_, innerIndex) => (
        <Skeleton key={innerIndex} className="h-10 w-full" />
       ))}
      </div>
     </div>
    </div>
   ))}
  </div>
 );
}

function AccessoryTypeCard({
 slug,
 displayName,
 productCount,
 parentTypeSlug,
}: {
 slug: string;
 displayName: string;
 productCount: number;
 parentTypeSlug: string;
}) {
 const description = ACCESSORY_DESCRIPTIONS[slug];
 const specs = ACCESSORY_KEY_SPECS[slug] ?? [];

 return (
  <Link
 href={`/accessories/${parentTypeSlug}/${slug}`}
 className="group block rounded-lg border border-border bg-card p-4 transition-all hover:border-primary "
  >
   <div className="flex items-start justify-between gap-2">
    <h3 className="font-semibold text-foreground group-hover:text-action">
     {displayName}s
    </h3>
    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
     {productCount}
    </span>
   </div>
   {description && (
    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
     {description}
    </p>
   )}
   {specs.length > 0 && (
    <ul className="mt-3 space-y-1">
     {specs.slice(0, 3).map((spec) => (
      <li key={spec} className="text-xs text-muted-foreground">
       • {spec}
      </li>
     ))}
    </ul>
   )}
   <div className="mt-3 flex items-center gap-1 text-sm font-medium text-brand-strong">
    View products
    <ArrowRight className="h-3.5 w-3.5" />
   </div>
  </Link>
 );
}

export function AccessoryCategoriesPageClient({
 parentTypeSlug,
}: {
 parentTypeSlug: string;
}) {
 const parentTypeName = PARENT_TYPE_NAMES[parentTypeSlug] ?? parentTypeSlug;

 const result = useQuery(api.products.accessoryQueries.listAccessories, {
 parentTypeSlug,
 page: 1,
 perPage: 500,
 });

 const breadcrumbItems: BreadcrumbItemType[] = [
  { label: "Home", href: "/" },
  { label: "Accessories", href: "/accessories" },
  { label: `${parentTypeName} Accessories`, current: true },
 ];

 const isLoading = result === undefined;
 const accessoryTypes = result?.accessoryTypes ?? [];
 const allListings = (result?.listings ?? []);
 const productsByType = accessoryTypes.map((type) => ({
  ...type,
 products: allListings.filter((product) => product.productTypeSlug === type.slug),
 }));
 const totalProducts = allListings.length;

 return (
  <div>
   <Container className="py-6">
    <Breadcrumbs items={breadcrumbItems} />

    <div className="mb-8 mt-4">
     <h1 className="text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl">
      {parentTypeName} Accessories
     </h1>
     {!isLoading && totalProducts > 0 && (
      <p className="mt-1 text-sm text-muted-foreground">
       {totalProducts} products across {accessoryTypes.length} categories
      </p>
     )}
    </div>

    {isLoading ? (
     <LoadingSkeleton />
    ) : allListings.length === 0 ? (
     <AccessoryEmptyCTA parentTypeName={parentTypeName} />
    ) : (
     <div className="space-y-12">
      <section>
       <h2 className="mb-4 text-lg font-semibold">Accessory Types</h2>
       <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {productsByType.map((category) => (
         <AccessoryTypeCard
 key={category.slug}
 slug={category.slug}
 displayName={category.displayName}
 productCount={category.products.length}
 parentTypeSlug={parentTypeSlug}
         />
        ))}
       </div>
      </section>

      {productsByType.map((category) => {
 if (category.products.length === 0) {
 return null;
       }

 const displayProducts = category.products.slice(0, 10);
 const hasMore = category.products.length > 10;

 return (
        <section key={category.slug} id={category.slug}>
         <div className="mb-3 flex items-end justify-between">
          <div>
           <h2 className="text-lg font-semibold">
            {category.displayName}s
            <span className="ml-2 text-sm font-normal text-muted-foreground">
             ({category.products.length})
            </span>
           </h2>
          </div>
          <Link
 href={`/accessories/${parentTypeSlug}/${category.slug}`}
 className="flex items-center gap-1 text-sm font-medium text-link hover:text-link-hover hover:underline"
          >
           View all
           <ArrowRight className="h-3.5 w-3.5" />
          </Link>
         </div>
         <AccessoryRecordTable
          objectId={`accessory-${category.slug}`}
          label={`${category.displayName}s`}
          products={displayProducts}
          definitions={getAccessoryColumnDefinitions(category.slug)}
         />
         {hasMore && (
          <div className="mt-3 text-center">
           <Link
 href={`/accessories/${parentTypeSlug}/${category.slug}`}
 className="text-sm text-muted-foreground hover:text-action"
           >
            +{category.products.length - 10} more products
           </Link>
          </div>
         )}
        </section>
       );
      })}
     </div>
    )}
   </Container>
  </div>
 );
}
